import { Box, Typography, TextField, Button, Grid, Chip } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useToast } from "components/Toast";
import ReUseAbleTable, { Column } from "shared/ReUseAbleTable";
import DashboardTopTitle from "components/DashboardTopTitle";
import {
  getSalesAnalytics,
  SalesResponse,
  FlightSale,
  RideSale,
} from "services/sales";
import StatsCards from "pages/AnalyticsPage/StatCards";

const SalesDashboard: React.FC = () => {
  const { showToast } = useToast();
  const today = new Date();
  const lastMonth = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    today.getDate(),
  );

  const [fromDate, setFromDate] = useState<Date | null>(lastMonth);
  const [toDate, setToDate] = useState<Date | null>(today);

  const [queryDates, setQueryDates] = useState<{
    from: Date | null;
    to: Date | null;
  }>({
    from: lastMonth,
    to: today,
  });

  const formattedQueryFromDate = queryDates.from
    ? format(queryDates.from, "yyyy-MM-dd")
    : "";
  const formattedQueryToDate = queryDates.to
    ? format(queryDates.to, "yyyy-MM-dd")
    : "";

  const {
    data: salesData,
    isLoading: isSalesLoading,
    error,
  } = useQuery<SalesResponse>({
    queryKey: ["sales", formattedQueryFromDate, formattedQueryToDate],
    queryFn: () =>
      getSalesAnalytics(formattedQueryFromDate, formattedQueryToDate),
    enabled: !!queryDates.from && !!queryDates.to,
  });

  // Toast once per error, not on every render (calling it during render
  // re-renders the toast provider, which re-renders this, in a loop).
  useEffect(() => {
    if (error) {
      showToast(
        error instanceof Error ? error.message : "Failed to fetch sales data",
        "error",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleReload = () => {
    setQueryDates({
      from: fromDate,
      to: toDate,
    });
  };

  const getStatusInfo = (status: string) => {
    const s = status.toLowerCase();
    if (s === "booked_successfully" || s === "success") {
      return {
        label: "Success",
        color: "#12B76A", // success
        bgColor: "rgba(18, 183, 106, 0.1)",
      };
    }
    if (s === "pending" || s === "pending_payment") {
      return {
        label: "Pending",
        color: "#F79009", // warning
        bgColor: "rgba(247, 144, 9, 0.1)",
      };
    }
    if (s === "expired" || s === "failed") {
      return {
        label: "Failed",
        color: "#F04438", // error
        bgColor: "rgba(240, 68, 56, 0.1)",
      };
    }
    return {
      label: status.replace(/_/g, " "),
      color: "#667085", // default
      bgColor: "rgba(102, 112, 133, 0.1)",
    };
  };

  const flightColumns: Column[] = [
    { id: "booking_id", label: "Booking ID", minWidth: 150 },
    { id: "pnr", label: "PNR", minWidth: 100 },
    { id: "airline", label: "Airline", minWidth: 150 },
    { id: "to", label: "Destination", minWidth: 200 },
    {
      id: "status",
      label: "Status",
      minWidth: 120,
      format: (value: string) => {
        const info = getStatusInfo(value);
        return (
          <Chip
            label={info.label}
            size="small"
            variant="outlined"
            sx={{
              textTransform: "capitalize",
              color: info.color,
              borderColor: "transparent",
              backgroundColor: info.bgColor,
              fontWeight: 500,
              borderRadius: "8px",
            }}
          />
        );
      },
    },
    {
      id: "flightPrice",
      label: "Service Price",
      minWidth: 120,
      align: "right",
      format: (value: number) => `₦${(value || 0).toLocaleString()}`,
    },
    {
      id: "amountPaid",
      label: "Amount Paid",
      minWidth: 120,
      align: "right",
      format: (value: number) => `₦${(value || 0).toLocaleString()}`,
    },
    {
      id: "profit",
      label: "Profit",
      minWidth: 100,
      align: "right",
      format: (value: number) => (
        <Typography
          fontSize="inherit"
          color={value >= 0 ? "success.main" : "error.main"}
        >
          ₦{(value || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      id: "dateTime",
      label: "Date & Time",
      minWidth: 150,
      format: (value: string) => (
        <Box>
          <Typography
            sx={{ color: "#101828", fontSize: "12px", fontWeight: 500 }}
          >
            {format(new Date(value), "d MMM, yyyy")}
          </Typography>
          <Typography
            sx={{ color: "#667085", fontSize: "11px", fontWeight: 400 }}
          >
            {format(new Date(value), "hh:mm a")}
          </Typography>
        </Box>
      ),
    },
  ];

  const rideColumns: Column[] = [
    { id: "traveller", label: "Traveller", minWidth: 150 },
    { id: "from", label: "From", minWidth: 180 },
    { id: "to", label: "To", minWidth: 180 },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      format: (value: string) => {
        const info = getStatusInfo(value);
        return (
          <Chip
            label={info.label}
            size="small"
            variant="outlined"
            sx={{
              textTransform: "capitalize",
              color: info.color,
              borderColor: "transparent",
              backgroundColor: info.bgColor,
              fontWeight: 500,
              borderRadius: "8px",
            }}
          />
        );
      },
    },
    {
      id: "ridePrice",
      label: "Ride Price",
      minWidth: 120,
      align: "right",
      format: (value: number) => `₦${(value || 0).toLocaleString()}`,
    },
    {
      id: "amountPaid",
      label: "Amount Paid",
      minWidth: 120,
      align: "right",
      format: (value: number) => `₦${(value || 0).toLocaleString()}`,
    },
    {
      id: "profit",
      label: "Profit",
      minWidth: 100,
      align: "right",
      format: (value: number) => (
        <Typography
          fontSize="inherit"
          color={value >= 0 ? "success.main" : "error.main"}
        >
          ₦{(value || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      id: "dateTime",
      label: "Date & Time",
      minWidth: 150,
      format: (value: string) => (
        <Box>
          <Typography
            sx={{ color: "#101828", fontSize: "12px", fontWeight: 500 }}
          >
            {format(new Date(value), "d MMM, yyyy")}
          </Typography>
          <Typography
            sx={{ color: "#667085", fontSize: "11px", fontWeight: 400 }}
          >
            {format(new Date(value), "hh:mm a")}
          </Typography>
        </Box>
      ),
    },
  ];

  const flightRows = salesData?.data?.flights?.data || [];
  const rideRows = salesData?.data?.rides?.data || [];

  const summary = salesData?.data?.summary;

  const combinedStats = summary
    ? [
        {
          id: 1,
          title: "Total Service Price (Combined)",
          value: `₦${Number(summary.combined.totalServicePrice || 0).toLocaleString()}`,
          subtitle: "Total price of all services",
          trend: 0,
          trendType: "up" as const,
        },
        {
          id: 2,
          title: "Total Amount Paid (Combined)",
          value: `₦${Number(summary.combined.totalAmountPaid || 0).toLocaleString()}`,
          subtitle: "Total revenue received",
          trend: 0,
          trendType: "up" as const,
        },
        {
          id: 3,
          title: "Total Profit (Combined)",
          value: `₦${Number(summary.combined.totalProfit || 0).toLocaleString()}`,
          subtitle: "Total profit across all services",
          trend: 0,
          trendType: "up" as const,
        },
      ]
    : [];

  const flightSummaryStats = summary
    ? [
        {
          id: 4,
          title: "Total Flight Price",
          value: `₦${Number(summary.flights.totalFlightPrice || 0).toLocaleString()}`,
          subtitle: "Sum of all flight prices",
          trend: 0,
          trendType: "up" as const,
        },
        {
          id: 5,
          title: "Total Flight Amount Paid",
          value: `₦${Number(summary.flights.totalAmountPaid || 0).toLocaleString()}`,
          subtitle: "Revenue from flight bookings",
          trend: 0,
          trendType: "up" as const,
        },
        {
          id: 6,
          title: "Total Flight Profit",
          value: `₦${Number(summary.flights.totalProfit || 0).toLocaleString()}`,
          subtitle: "Profit from flight bookings",
          trend: 0,
          trendType: "up" as const,
        },
      ]
    : [];

  const rideSummaryStats = summary
    ? [
        {
          id: 7,
          title: "Total Ride Price",
          value: `₦${Number(summary.rides.totalRidePrice || 0).toLocaleString()}`,
          subtitle: "Sum of all ride prices",
          trend: 0,
          trendType: "up" as const,
        },
        {
          id: 8,
          title: "Total Ride Amount Paid",
          value: `₦${Number(summary.rides.totalAmountPaid || 0).toLocaleString()}`,
          subtitle: "Revenue from ride bookings",
          trend: 0,
          trendType: "up" as const,
        },
        {
          id: 9,
          title: "Total Ride Profit",
          value: `₦${Number(summary.rides.totalProfit || 0).toLocaleString()}`,
          subtitle: "Profit from ride bookings",
          trend: 0,
          trendType: "up" as const,
        },
      ]
    : [];

  if (isSalesLoading) {
    return (
      <Box
        bgcolor="#FFFFFF"
        sx={{ borderRadius: "10px", border: "1px solid #E4E7EC" }}
        p={{ xs: 1, sm: 3 }}
      >
        <DashboardTopTitle
          title="Sales Dashboard"
          fontSize={20}
          subtitle="Loading sales data..."
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        bgcolor="#FFFFFF"
        sx={{
          borderRadius: "10px",
          border: { xs: "transparent", sm: "1px solid #E4E7EC" },
          mb: 3,
          p: { xs: 0, sm: 3 },
        }}
      >
        <DashboardTopTitle
          title="Sales Dashboard"
          fontSize={20}
          subtitle="Track and analyze sales performance across all services"
        />

        {/* Date Range Selector */}
        <Box mb={3} display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: 500, color: "#475467" }}
              >
                From:
              </Typography>
              <DatePicker
                value={fromDate}
                onChange={(date) => setFromDate(date)}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      minWidth: 180,
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#101828",
                        "& fieldset": { borderColor: "#E4E7EC" },
                        "&:hover fieldset": { borderColor: "#379E66" },
                        "&.Mui-focused fieldset": { borderColor: "#379E66" },
                      },
                    },
                  },
                }}
              />
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: 500, color: "#475467" }}
              >
                To:
              </Typography>
              <DatePicker
                value={toDate}
                onChange={(date) => setToDate(date)}
                minDate={fromDate || undefined}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      minWidth: 180,
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#101828",
                        "& fieldset": { borderColor: "#E4E7EC" },
                        "&:hover fieldset": { borderColor: "#379E66" },
                        "&.Mui-focused fieldset": { borderColor: "#379E66" },
                      },
                    },
                  },
                }}
              />
            </Box>
          </LocalizationProvider>

          <Button
            variant="contained"
            onClick={handleReload}
            sx={{
              bgcolor: "#0b1c12",
              "&:hover": { bgcolor: "#08150e" },
              height: "40px",
            }}
          >
            Reload
          </Button>
        </Box>

        {combinedStats.length > 0 && (
          <Box mb={4}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Combined Summary
            </Typography>
            <StatsCards stats={combinedStats} />

            <Typography variant="h6" fontWeight={600} mb={2} mt={3}>
              Flight Summary
            </Typography>
            <StatsCards stats={flightSummaryStats} />

            <Typography variant="h6" fontWeight={600} mb={2} mt={3}>
              Ride Summary
            </Typography>
            <StatsCards stats={rideSummaryStats} />
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            bgcolor="#FFFFFF"
            sx={{
              borderRadius: "10px",
              border: {
                xs: "transparent",
                sm: "1px solid #E4E7EC",
                p: { xs: 0, sm: 3 },
              },
            }}
          >
            <Typography variant="h6" fontWeight={600} mb={2}>
              Flight Sales{" "}
              <Typography
                component="span"
                variant="body1"
                sx={{ color: "#475467" }}
              >
                ({salesData?.data?.flights?.pagination?.total})
              </Typography>
            </Typography>
            <ReUseAbleTable columns={flightColumns} rows={flightRows} />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box
            bgcolor="#FFFFFF"
            sx={{
              borderRadius: "10px",
              border: { xs: "transparent", sm: "1px solid #E4E7EC" },
              p: { xs: 0, sm: 3 },
            }}
          >
            <Typography variant="h6" fontWeight={600} mb={2}>
              Ride Sales{" "}
              <Typography
                component="span"
                variant="body1"
                sx={{ color: "#475467" }}
              >
                ({salesData?.data?.rides?.pagination?.total})
              </Typography>
            </Typography>
            <ReUseAbleTable columns={rideColumns} rows={rideRows} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalesDashboard;
